import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { 
  BarChart3, RefreshCw, Loader2, Search, ChevronDown, ChevronRight,
  TrendingUp, Calendar, Users, Database
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FundPerformancePage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [fundsList, setFundsList] = useState([]);
  const [loadingFunds, setLoadingFunds] = useState(false);
  const [expandedQuarter, setExpandedQuarter] = useState(null);
  const [quarterFunds, setQuarterFunds] = useState({});

  // Fetch overall stats
  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/funds/performance-data-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      toast.error("Failed to load performance data stats");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch funds with performance data
  const fetchFundsWithPerformance = useCallback(async (query = "") => {
    setLoadingFunds(true);
    try {
      const response = await fetch(
        `${API}/funds/performance-data-funds?search=${encodeURIComponent(query)}&limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setFundsList(data.funds || []);
      }
    } catch (error) {
      console.error("Failed to fetch funds:", error);
    } finally {
      setLoadingFunds(false);
    }
  }, [token]);

  // Fetch funds for a specific quarter
  const fetchQuarterFunds = async (year, quarter) => {
    const key = `${year}-${quarter}`;
    if (quarterFunds[key]) {
      setExpandedQuarter(expandedQuarter === key ? null : key);
      return;
    }

    try {
      const response = await fetch(
        `${API}/funds/performance-data-by-quarter?year=${year}&quarter=${quarter}&limit=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setQuarterFunds(prev => ({ ...prev, [key]: data.funds || [] }));
        setExpandedQuarter(key);
      }
    } catch (error) {
      console.error("Failed to fetch quarter funds:", error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchFundsWithPerformance();
  }, [fetchStats, fetchFundsWithPerformance]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchFundsWithPerformance(searchQuery);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fund-performance-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Fund Performance Data</h1>
          <p className="text-sm text-muted-foreground">
            Quarterly performance metrics stored for point-in-time eligibility filtering
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchStats}
          disabled={loading}
          className="rounded-sm"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-sm">
                <Database className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Records</p>
                <p className="text-2xl font-bold font-mono">
                  {stats?.total_records?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-sm">
                <Users className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Unique Funds</p>
                <p className="text-2xl font-bold font-mono">
                  {stats?.unique_funds?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-sm">
                <Calendar className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Quarters Covered</p>
                <p className="text-2xl font-bold font-mono">
                  {stats?.quarters_covered || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded-sm">
                <TrendingUp className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Latest Quarter</p>
                <p className="text-2xl font-bold font-mono">
                  {stats?.latest_quarter || "N/A"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quarter Breakdown */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Records by Quarter
          </CardTitle>
          <CardDescription>
            Click a quarter to see sample funds
          </CardDescription>
        </CardHeader>
        <CardContent>
          {stats?.quarter_breakdown && stats.quarter_breakdown.length > 0 ? (
            <div className="space-y-2">
              {stats.quarter_breakdown.map((q) => {
                const [qPart, yearPart] = q.quarter.split("/");
                const year = parseInt(yearPart);
                const quarter = parseInt(qPart.replace("Q", ""));
                const key = `${year}-${quarter}`;
                const isExpanded = expandedQuarter === key;
                
                return (
                  <div key={q.quarter}>
                    <button
                      onClick={() => fetchQuarterFunds(year, quarter)}
                      className="w-full flex items-center justify-between p-3 bg-background/50 hover:bg-background/80 rounded-sm transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        )}
                        <span className="font-mono font-medium">{q.quarter}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground">
                          {q.count} records
                        </span>
                        <div className="w-32 bg-background rounded-full h-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full"
                            style={{
                              width: `${Math.min(100, (q.count / (stats.unique_funds || 1)) * 100)}%`
                            }}
                          />
                        </div>
                      </div>
                    </button>
                    
                    {isExpanded && quarterFunds[key] && (
                      <div className="ml-7 mt-2 mb-3 p-3 bg-background/30 rounded-sm">
                        <p className="text-xs text-muted-foreground mb-2">Sample funds:</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {quarterFunds[key].map((fund, idx) => (
                            <div key={idx} className="flex justify-between text-xs p-2 bg-background/50 rounded">
                              <span className="font-mono text-muted-foreground">{fund.cik}</span>
                              <span>
                                Hold: {fund.avg_holding_period?.toFixed(1) || "N/A"}mo | 
                                Size: {fund.portfolio_size || "N/A"}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No performance data available. Run the 13F pipeline to populate.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fund Search */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Search className="w-4 h-4" />
            Search Funds with Performance Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-2 mb-4">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by CIK or fund name..."
              className="bg-background rounded-sm"
            />
            <Button type="submit" variant="outline" className="rounded-sm" disabled={loadingFunds}>
              {loadingFunds ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </form>

          {fundsList.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-2 px-3 font-medium text-muted-foreground">CIK</th>
                    <th className="py-2 px-3 font-medium text-muted-foreground">Fund Name</th>
                    <th className="py-2 px-3 font-medium text-muted-foreground text-right">Quarters</th>
                    <th className="py-2 px-3 font-medium text-muted-foreground text-right">Avg Holding</th>
                    <th className="py-2 px-3 font-medium text-muted-foreground text-right">Avg Size</th>
                  </tr>
                </thead>
                <tbody>
                  {fundsList.map((fund, idx) => (
                    <tr key={idx} className="border-b border-border/50 hover:bg-background/50">
                      <td className="py-2 px-3 font-mono text-xs">{fund.cik}</td>
                      <td className="py-2 px-3 truncate max-w-[200px]">{fund.investor_name || "Unknown"}</td>
                      <td className="py-2 px-3 text-right font-mono">{fund.quarters_count}</td>
                      <td className="py-2 px-3 text-right font-mono">
                        {fund.avg_holding_period?.toFixed(1) || "N/A"}mo
                      </td>
                      <td className="py-2 px-3 text-right font-mono">
                        {fund.avg_portfolio_size?.toLocaleString() || "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground text-sm">
              {loadingFunds ? "Loading..." : "No funds found"}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FundPerformancePage;
